import React, { FunctionComponent, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import Alert from '@justgiving/alert';

import {
  AlertTypes,
  QueryMessages,
  QueryParameters,
} from '../../constants/errorTypes';
import { useIsMounted } from '../../hooks/useIsMounted';
import { usePageMeta } from '../../hooks';

const PageAlert: FunctionComponent = () => {
  const [showAlert, setShowAlert] = useState<boolean>(true);
  const isMounted = useIsMounted();
  const { displayAlertOnly } = usePageMeta();

  // useState/useEffect hack used to force a single re-render, which is required for the PageAlert component to display
  // TODO: Find a better solution to make the PageAlert display in `displayAlertOnly` mode
  const [, setValue] = useState(false);
  useEffect(() => {
    setValue(true);
  }, []);

  if (!isMounted || !window.location.search) {
    return null;
  }

  const urlParams = new URLSearchParams(window.location.search);

  const alertType: AlertTypes =
    (urlParams?.get(QueryParameters.ALERT_TYPE) as AlertTypes) ??
    AlertTypes.info;

  const alertMessageKey = urlParams.get(QueryParameters.ALERT_MESSAGE);

  if (!showAlert || !alertMessageKey) {
    return null;
  }

  return (
    <Alert
      type={alertType}
      onClose={displayAlertOnly ? undefined : () => setShowAlert(false)}
    >
      {getPageAlertTranslation(alertMessageKey)}
    </Alert>
  );
};

function getPageAlertTranslation(key: string) {
  switch (true) {
    case key === QueryMessages.DONATION_ERROR: {
      return (
        <Trans i18nKey="page.alert.donationError">
          Looks like we're running into some trouble. You can try donating
          again, or{' '}
          <a
            href="https://help.justgiving.com/hc/en-us/requests/new?ticket_form_id=5342"
            target="_blank"
            rel="noopener noreferrer"
          >
            reach out to our Customer Support team for help
          </a>
          .
        </Trans>
      );
    }
    case key === QueryMessages.DONATION_ERROR_NOT_SUPPORTED: {
      return (
        <Trans i18nKey="page.alert.donationErrorNotSupported">
          We're sorry - we're not able to process your donation to this charity.{' '}
          <a
            href="https://help.justgiving.com/hc/en-us/articles/360009270137-Why-can-t-I-donate-to-certain-charities-outside-of-the-EU-"
            target="_blank"
            rel="noopener noreferrer"
          >
            Find out why
          </a>
          .
        </Trans>
      );
    }
    case key.includes(
      QueryMessages.FITNESS_AUTH_ERROR_STRAVA_ALREADY_CONNECTED,
    ): {
      return <Trans i18nKey="page.alert.fitnessAuthErrorStrava" />;
    }
    case key.includes(
      QueryMessages.FITNESS_AUTH_ERROR_FITBIT_ALREADY_CONNECTED,
    ): {
      return <Trans i18nKey="page.alert.fitnessAuthErrorFitbit" />;
    }
    case key.includes(QueryMessages.FITNESS_AUTH_ERROR): {
      return <Trans i18nKey="page.alert.fitnessAuthError" />;
    }
    default: {
      return <Trans i18nKey="page.errorGeneric.heading" />;
    }
  }
}

export default PageAlert;
